import { formatTimeForHumanoids } from "../../../../utils/date-time/format-time-for-humanoids.js";
import { Alerts } from "../../../../utils/Alerts.js";
import { usePublishingContext } from "../../../../contexts/PublishingContext.js";
import { FormikToggleSwitch } from "../../../../components/FormikFields/FormikToggleSwitch.js";
import { useDownloadsApiServiceContext } from "../../../../contexts/DownloadsApiServiceContext.js";
import { useSessionStorage } from "../../../../hooks/useSessionStorage.js";
import { useCurrentUserAnPCheck } from "../../../AuthWrapper2/hooks/useCurrentUserAnPCheck.js";
import { useLocationLogEntries } from "../../hooks/useLocationLogEntries.js";
import { useLocationContext } from "../../contexts/LocationContext.js";
import { locationLogEntriesSummaryDownload } from "./location-log-entries-summary-download.js";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { Button, DatePicker, FormLayout, Icon, Loading, Modal, toastManager } from "@therms/atalaya";
import { IoMdDownload } from "react-icons/io";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import { downloadBase64AsPdf, getFormattedRelativeDate, pluralize } from "@therms/web-js";
import classnames from "classnames";
import { MdDataset, MdDescription, MdRefresh } from "react-icons/md";
import { Formik } from "formik";
import * as Yup from "yup";
//#region src/modules/Locations/shared/LocationLogEntries/DownloadLogEntriesModal.tsx
var ValidationSchema = Yup.object().shape({
	showEventsRefs: Yup.boolean(),
	showGpsCoords: Yup.boolean(),
	showInternalContent: Yup.boolean(),
	showRecordsRefs: Yup.boolean(),
	showReportsRefs: Yup.boolean(),
	showUsersRefs: Yup.boolean(),
	withRefHistory: Yup.boolean()
});
var initialValues = {
	showEventsRefs: false,
	showGpsCoords: false,
	showInternalContent: false,
	showLocationsRefs: false,
	showRecordsRefs: false,
	showReportsRefs: false,
	showUsersRefs: false,
	withRefHistory: false
};
function DownloadLogEntriesModal({ activityTypeIds, closeHandler, entryTypeIds, showInternalContent, showReferences, userIds }) {
	const { client } = useDownloadsApiServiceContext();
	const { location, locationId } = useLocationContext(true);
	const [sessionValue, setSessionValue] = useSessionStorage("downloadLogEntriesOptions");
	const publishingCtx = usePublishingContext();
	const [startTimestamp, setStartTimestamp] = useState();
	const [endTimestamp, setEndTimestamp] = useState();
	const [downloading, setDownloading] = useState(false);
	const [csvDownload, setCSVDownload] = useState(false);
	const [excelDownload, setExcelDownload] = useState(false);
	const [isPickingReferences, setIsPickingReferences] = useState(false);
	const hasAccessToGpsCoords = useCurrentUserAnPCheck((user) => user.anp.role.portal.access.viewUserGps || user.anp.role.ops.access.viewOnDutyUsersGeoLocation);
	const fromDate = startTimestamp || "";
	let toDate = "";
	if (endTimestamp) toDate = endTimestamp;
	else if (startTimestamp) toDate = dayjs(startTimestamp).endOf("day").toISOString();
	const { data: entriesData, loading: entriesByDateDataLoading } = useLocationLogEntries({
		activityTypeIds,
		entryTypeIds,
		fromDate,
		locationId,
		pagination: { limit: 5e3 },
		toDate,
		userIds
	});
	const handleLogEntriesPDFDownload = (values) => {
		if (!startTimestamp) return;
		const toastId = Alerts.info(/* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx(Icon, {
			Icon: MdRefresh,
			spin: true
		}), " Starting download, please wait..."] }), {
			autoClose: false,
			position: "top-center"
		});
		setDownloading(true);
		client.locations.DownloadLocationLogEntries({ body: {
			activityTypeIds,
			entryTypeIds,
			fromDate,
			locationId,
			showEventsRefs: values.showEventsRefs,
			showGpsCoords: values.showGpsCoords,
			showInternalContent: values.showInternalContent,
			showLocationsRefs: values.showLocationsRefs,
			showRecordsRefs: values.showRecordsRefs,
			showReportsRefs: values.showReportsRefs,
			showUsersRefs: values.showUsersRefs,
			toDate,
			userIds,
			withRefHistory: values.withRefHistory
		} }, { timeout: 3e5 }).then((response) => {
			const result = response;
			if (result.pdfBase64) downloadBase64AsPdf(`${location?.name} Log Entries - ${dayjs(startTimestamp).format()}`, result.pdfBase64);
		}).catch((err) => {
			console.error("err downloading entries", err);
			Alerts.error(/* @__PURE__ */ jsx("span", {
				className: "text-color-critical",
				children: "'There was a problem downloading the entries, check your connection and try again at a later time.'"
			}));
		}).finally(() => {
			toastManager.dismiss(toastId);
			setDownloading(false);
		});
	};
	const handleLogEntriesSummaryDownload = (format) => {
		const entries = entriesData?.entries;
		if (!Array.isArray(entries) || !entries.length) return;
		locationLogEntriesSummaryDownload(entries, { locationsById: { [locationId]: {
			address: location.address,
			idLabel: location.id,
			name: location.name
		} } }, {
			fileName: `${location.name} Log Entry Summary ${formatTimeForHumanoids(startTimestamp || "")} - ${getFormattedRelativeDate(endTimestamp || "")}`,
			format
		});
	};
	const displayInternalContent = useMemo(() => {
		if (publishingCtx) return publishingCtx.showInternalContent;
		else return showInternalContent;
	}, [publishingCtx, showInternalContent]);
	return /* @__PURE__ */ jsx(Modal, {
		closeHandler,
		escPressClose: true,
		header: "Download Log Entries",
		children: /* @__PURE__ */ jsxs("div", {
			className: "flex min-h-[400px] flex-wrap",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "bg-surface-subtle p-base flex flex-1 flex-col rounded",
				children: [/* @__PURE__ */ jsx("p", { children: "Please pick a date or date range to download all log entries within that period." }), !!startTimestamp && /* @__PURE__ */ jsxs("div", {
					className: "mt-base",
					children: [
						entriesByDateDataLoading ? /* @__PURE__ */ jsx("div", {
							className: "text-md text-color-subtle",
							children: /* @__PURE__ */ jsx(Loading, {
								message: "Looking for entries",
								size: "sm"
							})
						}) : /* @__PURE__ */ jsxs("div", {
							className: "text-md text-color-info",
							children: [
								"Found ",
								entriesData?.total,
								" ",
								pluralize(["entry", "entries"], entriesData?.total),
								" to download"
							]
						}),
						/* @__PURE__ */ jsx("div", {
							className: "mt-xl px-sm text-color-subtle text-xs font-bold tracking-wider uppercase",
							children: "Download"
						}),
						/* @__PURE__ */ jsxs("div", {
							className: "mt-xxs gap-x-xs border-surface p-xs flex justify-center rounded border",
							children: [
								/* @__PURE__ */ jsxs(Button, {
									active: isPickingReferences,
									className: "grow",
									disabled: !startTimestamp || downloading,
									onClick: () => {
										setCSVDownload(false);
										setExcelDownload(false);
										setIsPickingReferences(true);
									},
									size: "sm",
									subtle: true,
									variant: isPickingReferences ? "main" : "neutral",
									children: [/* @__PURE__ */ jsx(Icon, {
										Icon: MdDescription,
										className: "mr-xxs"
									}), /* @__PURE__ */ jsx("span", { children: "PDF" })]
								}),
								/* @__PURE__ */ jsxs(Button, {
									className: "grow",
									disabled: !startTimestamp || downloading,
									onClick: () => {
										setCSVDownload(true);
										setExcelDownload(false);
										setIsPickingReferences(false);
									},
									size: "sm",
									subtle: true,
									children: [/* @__PURE__ */ jsx(Icon, {
										Icon: MdDataset,
										className: "mr-xxs"
									}), /* @__PURE__ */ jsx("span", { children: "CSV" })]
								}),
								/* @__PURE__ */ jsxs(Button, {
									className: "grow",
									disabled: !startTimestamp || downloading,
									onClick: () => {
										setCSVDownload(false);
										setExcelDownload(true);
										setIsPickingReferences(false);
									},
									size: "sm",
									subtle: true,
									children: [/* @__PURE__ */ jsx(Icon, {
										Icon: MdDataset,
										className: "mr-xxs"
									}), /* @__PURE__ */ jsx("span", { children: "Excel" })]
								})
							]
						}),
						isPickingReferences && /* @__PURE__ */ jsx("div", {
							className: classnames({ "p-sm": displayInternalContent || showReferences }),
							children: /* @__PURE__ */ jsx(Formik, {
								initialValues: sessionValue || initialValues,
								onSubmit: (values) => {
									setSessionValue(values);
									handleLogEntriesPDFDownload(values);
								},
								ValidationSchema,
								children: ({ handleSubmit, values }) => /* @__PURE__ */ jsxs(FormLayout, {
									space: displayInternalContent || showReferences ? "xs" : void 0,
									children: [(hasAccessToGpsCoords || displayInternalContent || showReferences) && /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsxs(FormLayout.Section, { children: [
										/* @__PURE__ */ jsx("div", {
											className: "text-color-subtle font-bold",
											children: "PDF Details"
										}),
										hasAccessToGpsCoords && /* @__PURE__ */ jsx(FormikToggleSwitch, {
											active: values.showGpsCoords,
											name: "showGpsCoords",
											label: "Include GPS Coordinates?"
										}),
										displayInternalContent && /* @__PURE__ */ jsx(FormikToggleSwitch, {
											active: values.showInternalContent,
											name: "showInternalContent",
											label: "Include Internal Content?"
										})
									] }), showReferences && /* @__PURE__ */ jsxs(FormLayout.Section, { children: [
										/* @__PURE__ */ jsx("div", {
											className: "text-color-subtle font-bold",
											children: "References"
										}),
										/* @__PURE__ */ jsx(FormikToggleSwitch, {
											active: values.withRefHistory,
											name: "withRefHistory",
											label: /* @__PURE__ */ jsx("div", {
												className: "text-color",
												children: "Include the Reference History?"
											}),
											subText: "A timestamped list of all references for each Log Entry."
										}),
										/* @__PURE__ */ jsx(FormikToggleSwitch, {
											active: values.showEventsRefs,
											name: "showEventsRefs",
											label: "Include Event References?"
										}),
										/* @__PURE__ */ jsx(FormikToggleSwitch, {
											active: values.showLocationsRefs,
											name: "showLocationsRefs",
											label: "Include Location References?"
										}),
										/* @__PURE__ */ jsx(FormikToggleSwitch, {
											active: values.showRecordsRefs,
											name: "showRecordsRefs",
											label: "Include Record References?"
										}),
										/* @__PURE__ */ jsx(FormikToggleSwitch, {
											active: values.showReportsRefs,
											name: "showReportsRefs",
											label: "Include Report References?"
										}),
										/* @__PURE__ */ jsx(FormikToggleSwitch, {
											active: values.showUsersRefs,
											name: "showUsersRefs",
											label: "Include User References?"
										})
									] })] }), /* @__PURE__ */ jsx("div", {
										className: classnames({ "mt-xs": displayInternalContent || showReferences }),
										children: /* @__PURE__ */ jsx(FormLayout.Controls, {
											disabled: downloading,
											isHorizontal: false,
											isSaving: downloading,
											onSubmit: handleSubmit,
											submitLabel: /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(Icon, {
												Icon: IoMdDownload,
												className: "mr-xxs"
											}), "Download"] })
										})
									})]
								})
							})
						}),
						(csvDownload || excelDownload) && /* @__PURE__ */ jsx("div", {
							className: "pt-sm",
							children: /* @__PURE__ */ jsx(Button, {
								fullWidth: true,
								variant: "main",
								onClick: () => {
									if (csvDownload) handleLogEntriesSummaryDownload("csv");
									if (excelDownload) handleLogEntriesSummaryDownload("excel");
								},
								children: /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(Icon, {
									Icon: IoMdDownload,
									className: "mr-xxs"
								}), "Download"] })
							})
						})
					]
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "p-base mx-auto",
				children: /* @__PURE__ */ jsx(DatePicker, {
					onChangeValue: (date) => {
						setStartTimestamp(date.start);
						setEndTimestamp(date.end);
					},
					rangePick: true,
					timePick: true,
					timestampStart: startTimestamp,
					timestampEnd: endTimestamp
				})
			})]
		})
	});
}
//#endregion
export { DownloadLogEntriesModal };

//# sourceMappingURL=DownloadLogEntriesModal.js.map